import { useState } from 'react';
import { useLockBodyScroll } from './useLockBodyScroll';

export default function VerifyContactModal({
  isOpen,
  onClose,
  contact,
  onVerified,
  trads,
}) {
  useLockBodyScroll(isOpen);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const t = trads?.contacts || {};

  const handleClose = () => {
    setCode('');
    setError('');
    setLoading(false);
    onClose();
  };

  const handleVerify = async () => {
    if (!code || code.trim().length < 4) return;
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/contacts/verify-contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contactId: contact.id,
          code: code.trim(),
        }),
      });

      const data = await res.json();
      if (data.success && data.contact) {
        onVerified(data.contact);
        handleClose();
      } else {
        setError(
          data.error || t.failedToVerifyContact || 'Invalid verification code'
        );
      }
    } catch (err) {
      console.error('Error verifying contact:', err);
      setError(t.errorVerifyingContact || 'Error verifying contact');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/contacts/update-or-create-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: contact.id,
          name: contact.name,
          phone: contact.phone,
          countryCode: contact.countryCode,
        }),
      });
      const data = await res.json();
      if (data.success && data.verificationPending) {
        // Success
      } else {
        setError(data.error || 'Failed to resend code');
      }
    } catch (err) {
      setError('Error resending code');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !contact) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {t.verifyContactTitle || 'Verify Contact'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {t.verificationSmsSentTo ||
              'A SMS has been sent to this contact for verification, please provide the verification code they have received in order to validate contact.'}
          </p>
          <p className="text-sm font-medium text-gray-900">
            {contact.name} ({contact.phone})
          </p>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.verificationCode || 'Verification Code'}
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              maxLength={10}
            />
          </div>

          <button
            type="button"
            onClick={handleResend}
            disabled={loading}
            className="text-sm text-indigo-600 hover:text-indigo-800 underline"
          >
            {t.resendCode || 'Resend verification code'}
          </button>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            {t.cancel || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleVerify}
            disabled={loading || !code}
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg disabled:opacity-50"
          >
            {loading ? t.verifying || 'Verifying...' : t.verify || 'Verify'}
          </button>
        </div>
      </div>
    </div>
  );
}
